import axios from 'axios';
import { apiClient } from './apiClient';
import { AuthResponse, LoginCredentials, ChangePasswordData, User } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class AuthService {
  private api = apiClient;

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', credentials);
    const { tokens } = response.data.data;
    this.setTokens(tokens.accessToken, tokens.refreshToken);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } finally {
      this.clearTokens();
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<{ success: boolean; data: { user: User } }>('/auth/me');
    return response.data.data.user;
  }

  async changePassword(data: ChangePasswordData): Promise<void> {
    await this.api.post('/auth/change-password', data);
  }

  async refreshAccessToken(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken,
    });

    const { tokens } = response.data.data;
    this.setTokens(tokens.accessToken, tokens.refreshToken);
  }

  // Token management
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }
}

export const authService = new AuthService();

